import { QueryClient } from '@tanstack/vue-query';
import { ApiError } from '@/shared/api/problem';

/**
 * El cliente de consultas, creado explícitamente en vez de dejárselo al plugin.
 *
 * Hace falta una referencia porque la caché pertenece a una identidad: al cambiar de
 * usuario hay que descartarla, y para eso alguien tiene que poder alcanzarla desde afuera
 * de un componente.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // API-011 — retry only what could plausibly succeed on a second attempt. Retrying
      // a 403 or a 404 adds load and latency and changes nothing.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.isRetryable && failureCount < 2,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

/**
 * Descarta todo lo que quedó en memoria de la sesión anterior.
 *
 * La caché no es «datos de la aplicación», es «lo que este usuario ya pidió». Sobrevivir a
 * un cambio de sesión hacía que la persona siguiente viera de entrada lo que había pedido
 * la anterior —el panel de pendientes con nombres y ausencias de otro— hasta que cada
 * consulta se revalidara. Un F5 lo tapaba, que es lo que hacía difícil de ver el problema.
 */
export function clearIdentityCache(): void {
  // `clear()` y no `invalidateQueries()`: invalidar deja los datos viejos visibles
  // mientras revalida, y son justamente los que no hay que mostrar.
  queryClient.clear();
}
